import type { Option } from '@branchjs/neo-components';
import { deepCopy } from '../../../../../../shared/utils/deepCopy';
import type { IQrCodeLink } from '../../../../../../shared/types';
import { LINK_TYPE_QR_CODE } from '../../../../../../shared/constants';
import { LinkAdapterService, Redirect } from 'client/js/main/services/LinkAdapterService/LinkAdapterService';
import type { QrCodeLinkCreatorState } from '../types';

export type EditQrCodeLinkPayload = Omit<
  QrCodeLinkCreatorState,
  | 'step'
  | 'defineStepVisited'
  | 'analyticTagsStepVisited'
  | 'redirectsStepVisited'
  | 'customLinkDataStepVisited'
  | 'customizeStepVisited'
  | 'hasBlacklistError'
>;

export type DuplicateQrCodeLinkPayload = Omit<EditQrCodeLinkPayload, 'id' | 'qrSettingsId'>;

export class QrCodeLinkAdapterService {
  private linkAdapterService: LinkAdapterService;

  constructor() {
    this.linkAdapterService = new LinkAdapterService(LINK_TYPE_QR_CODE);
  }

  private toOption(value?: string): undefined | Option {
    return value === null || value === undefined ? undefined : { value, label: value };
  }

  private toRedirectValue(redirect: Redirect, value: string | undefined): undefined | string | Option {
    return redirect === Redirect.Deepview ? this.toOption(value) : value;
  }

  private toAnalyticTagsState(analyticsTags: IQrCodeLink['analyticsTags']) {
    const feature = this.toOption(analyticsTags?.feature);
    const channel = this.toOption(analyticsTags?.channel);
    const campaign = this.toOption(analyticsTags?.campaign);
    const tags = (analyticsTags?.tags || []).map((v) => this.toOption(v)).filter((v) => !!v) as Option[];
    return { feature, channel, campaign, tags };
  }

  private toRedirectState(redirects: IQrCodeLink['redirects']) {
    const iosRedirect = this.linkAdapterService.getRedirectType(redirects?.ios?.override);
    const ios = this.toRedirectValue(iosRedirect, redirects?.ios?.value);
    const androidRedirect = this.linkAdapterService.getRedirectType(redirects?.android?.override);
    const android = this.toRedirectValue(androidRedirect, redirects?.android?.value);
    const isWebOnly = !!redirects?.isWebOnly;
    return { iosRedirect, ios, androidRedirect, android, isWebOnly };
  }

  private toLinkDataOptions(linkData: IQrCodeLink['linkData']) {
    if (!linkData) {
      return [];
    }
    return this.linkAdapterService.getLinkDataOptions(linkData);
  }

  private toQrSettingsId(qrCode: IQrCodeLink['qrCode']) {
    return qrCode?.qr_settings_id;
  }

  private toQrCodeCustomization(qrCode: IQrCodeLink['qrCode']) {
    const customizations = deepCopy(qrCode);

    delete customizations?.qr_settings_id;

    if (!customizations || Object.keys(customizations).length === 0) {
      return {};
    }

    // prevents app settings from overriding the qr code settings
    const appQrCodeSettingOverrides = {
      center_logo_url: '',
      background_image_url: '',
      code_pattern_url: '',
    };

    return {
      ...appQrCodeSettingOverrides,
      ...customizations,
    };
  }

  toEditPayload(apiQrCodeLink: IQrCodeLink): EditQrCodeLinkPayload {
    return {
      id: apiQrCodeLink.id,
      qrSettingsId: this.toQrSettingsId(apiQrCodeLink.qrCode),
      ...this.toDuplicatePayload(apiQrCodeLink),
    };
  }

  toDuplicatePayload(apiQrCodeLink: IQrCodeLink): DuplicateQrCodeLinkPayload {
    return {
      title: apiQrCodeLink.title,
      ...this.toAnalyticTagsState(apiQrCodeLink.analyticsTags),
      ...this.toRedirectState(apiQrCodeLink.redirects),
      linkData: this.toLinkDataOptions(apiQrCodeLink.linkData),
      qrCodeCustomization: this.toQrCodeCustomization(apiQrCodeLink.qrCode),
    };
  }
}
